import * as React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import CssBaseline from '@mui/material/CssBaseline';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import FormLabel from '@mui/material/FormLabel';
import FormControl from '@mui/material/FormControl';
import Link from '@mui/material/Link';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import MuiCard from '@mui/material/Card';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import { styled } from '@mui/material/styles';
import AppTheme from '../AppTheme';
import ColorModeSelect from '../customizations/ColorModeSelect';
import Sitemark from '../components/SitemarkIcon';
import config from '../config';

const API_BASE_URL = `${config.server}/api`;
const PROD_HOSTS = ['economicmessage.com', 'www.economicmessage.com', 'charity.dpdns.org'];
const cloudflare_key = PROD_HOSTS.includes(window.location.hostname)
  ? '0x4AAAAAAB1oTW9OdFTzJZyh'
  : '1x00000000000000000000AA';

const Card = styled(MuiCard)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignSelf: 'center',
  width: '100%',
  padding: theme.spacing(4),
  gap: theme.spacing(2),
  margin: 'auto',
  [theme.breakpoints.up('sm')]: {
    maxWidth: '450px',
  },
  boxShadow:
    'hsla(220, 30%, 5%, 0.05) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.05) 0px 15px 35px -5px',
  ...theme.applyStyles('dark', {
    boxShadow:
      'hsla(220, 30%, 5%, 0.5) 0px 5px 15px 0px, hsla(220, 25%, 10%, 0.08) 0px 15px 35px -5px',
  }),
}));

const SignInContainer = styled(Stack)(({ theme }) => ({
  height: 'calc((1 - var(--template-frame-height, 0)) * 100dvh)',
  minHeight: '100%',
  padding: theme.spacing(2),
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(4),
  },
  '&::before': {
    content: '""',
    display: 'block',
    position: 'absolute',
    zIndex: -1,
    inset: 0,
    backgroundImage:
      'radial-gradient(ellipse at 50% 50%, hsl(210, 100%, 97%), hsl(0, 0%, 100%))',
    backgroundRepeat: 'no-repeat',
    ...theme.applyStyles('dark', {
      backgroundImage:
        'radial-gradient(at 50% 50%, hsla(210, 100%, 16%, 0.5), hsl(220, 30%, 5%))',
    }),
  },
}));

// 稳定的 Cloudflare Turnstile 组件
const CloudflareTurnstile = ({ onVerify, onError }) => {
  const turnstileRef = React.useRef(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [hasError, setHasError] = React.useState(false);
  const widgetIdRef = React.useRef(null);
  const isInitializedRef = React.useRef(false);
  
  React.useEffect(() => {
    // 防止重复初始化
    if (isInitializedRef.current || !window.turnstile || !turnstileRef.current) {
      return;
    }

    const initTurnstile = async () => {
      try {
        setIsLoading(true);
        setHasError(false);
        
        // 确保容器存在且为空
        if (turnstileRef.current) {
          turnstileRef.current.innerHTML = '';
        }
        
        // 等待一个渲染周期
        await new Promise(resolve => setTimeout(resolve, 100));
        
        if (!turnstileRef.current) return;
        
        // 创建 Turnstile 实例
        widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
          sitekey: cloudflare_key,
          callback: (token) => {
            console.log('Turnstile verified successfully');
            setIsLoading(false);
            onVerify(token);
          },
          'error-callback': (error) => {
            console.error('Turnstile error:', error);
            setHasError(true);
            setIsLoading(false);
            onError(error);
          },
          'expired-callback': () => {
            console.warn('Turnstile expired, resetting...');
            // 过期时重置状态，但不触发错误
            setIsLoading(true);
            setTimeout(() => {
              if (window.turnstile && widgetIdRef.current !== null) {
                window.turnstile.reset(widgetIdRef.current);
                setIsLoading(false);
              }
            }, 500);
          },
          'timeout-callback': () => {
            console.warn('Turnstile timeout, retrying...');
            setHasError(true);
            setIsLoading(false);
            onError('timeout');
          },
          theme: 'auto',
          size: 'normal',
        });
        
        isInitializedRef.current = true;
        setIsLoading(false);
        
      } catch (error) {
        console.error('Failed to initialize Turnstile:', error);
        setHasError(true);
        setIsLoading(false);
        onError(error);
      }
    };

    initTurnstile();
    
    // 清理函数
    return () => {
      if (widgetIdRef.current !== null && window.turnstile) {
        try {
          window.turnstile.remove(widgetIdRef.current);
          widgetIdRef.current = null;
        } catch (e) {
          // 静默处理清理错误
        }
      }
      isInitializedRef.current = false;
    };
  }, []); // 空依赖数组，只在组件挂载时执行一次
  
  const handleRetry = React.useCallback(() => {
    setHasError(false);
    setIsLoading(true);
    isInitializedRef.current = false;
    
    // 强制重新渲染
    setTimeout(() => {
      if (turnstileRef.current && window.turnstile) {
        turnstileRef.current.innerHTML = '';
        
        try {
          widgetIdRef.current = window.turnstile.render(turnstileRef.current, {
            sitekey: cloudflare_key,
            callback: (token) => {
              console.log('Turnstile verified successfully (retry)');
              setIsLoading(false);
              onVerify(token);
            },
            'error-callback': (error) => {
              console.error('Turnstile error (retry):', error);
              setHasError(true);
              setIsLoading(false);
              onError(error);
            },
            'expired-callback': () => {
              console.warn('Turnstile expired (retry)');
              setIsLoading(true);
              setTimeout(() => {
                if (window.turnstile && widgetIdRef.current !== null) {
                  window.turnstile.reset(widgetIdRef.current);
                  setIsLoading(false);
                }
              }, 500);
            },
            theme: 'auto',
            size: 'normal',
          });
          
          isInitializedRef.current = true;
          setIsLoading(false);
        } catch (error) {
          console.error('Failed to retry Turnstile:', error);
          setHasError(true);
          setIsLoading(false);
        }
      }
    }, 100);
  }, [onVerify, onError]);
  
  if (hasError) {
    return (
      <Box sx={{ textAlign: 'center', p: 2, minHeight: '65px' }}>
        <Typography color="error" variant="body2" gutterBottom>
          真人验证失败
        </Typography>
        <Button size="small" onClick={handleRetry} variant="outlined">
          重新验证
        </Button>
      </Box>
    );
  }
  
  return (
    <Box sx={{ position: 'relative', minHeight: '65px', width: '100%' }}>
      {isLoading && (
        <Box 
          sx={{ 
            position: 'absolute', 
            top: '50%', 
            left: '50%', 
            transform: 'translate(-50%, -50%)',
            zIndex: 1
          }}
        >
          <CircularProgress size={24} />
        </Box>
      )}
      <div 
        ref={turnstileRef} 
        style={{ 
          width: '100%', 
          display: 'flex', 
          justifyContent: 'center',
          minHeight: '65px'
        }}
      ></div>
    </Box>
  );
};

export default function SignIn(props) {
  const [usernameError, setUsernameError] = React.useState(false);
  const [usernameErrorMessage, setUsernameErrorMessage] = React.useState('');
  const [passwordError, setPasswordError] = React.useState(false);
  const [passwordErrorMessage, setPasswordErrorMessage] = React.useState('');
  const [rememberMe, setRememberMe] = React.useState(false);
  const [cfToken, setCfToken] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [alertMessage, setAlertMessage] = React.useState('');
  const [alertSeverity, setAlertSeverity] = React.useState('error');
  const [scriptLoaded, setScriptLoaded] = React.useState(false);

  const validateInputs = () => {
    const username = document.getElementById('username').value;
    const password = document.getElementById('password').value;

    let isValid = true;

    // 用戶名驗證：3-20位英文小寫字母
    if (!username || !/^[a-z]{3,20}$/.test(username)) {
      setUsernameError(true);
      setUsernameErrorMessage('用戶名必須為3-20位英文小寫字母');
      isValid = false;
    } else {
      setUsernameError(false);
      setUsernameErrorMessage('');
    }

    // 密碼驗證：原始複雜度要求
    if (!password || !/^(?=.*[A-Za-z])(?=.*\d)(?=.*[@$!%*?&,])[A-Za-z\d@$!%*?&,]{13,30}$/.test(password)) {
      setPasswordError(true);
      setPasswordErrorMessage('密碼不符合要求');
      isValid = false;
    } else {
      setPasswordError(false);
      setPasswordErrorMessage('');
    }

    return isValid;
  };

  const handleCloudflareCBSuccess = React.useCallback((token) => {
    console.log('Received Cloudflare token:', token);
    setCfToken(token);
    setAlertMessage(''); // 清除任何之前的错误信息
  }, []);

  const handleCloudflareError = React.useCallback((error) => {
    console.error('Cloudflare error:', error);
    setCfToken(''); // 重置 token
    
    let errorMessage = '真人验证失败，请重试';
    if (error === 'expired') {
      errorMessage = '验证已过期，请重新验证';
    } else if (error === 'timeout') {
      errorMessage = '验证超时，请重新尝试';
    }
    
    setAlertMessage(errorMessage);
    setAlertSeverity('warning');
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (!validateInputs()) {
      return;
    }

    if (!cfToken) {
      setAlertMessage('請完成真人驗證');
      setAlertSeverity('warning');
      return;
    }

    setLoading(true);
    setAlertMessage('');

    const data = new FormData(event.currentTarget);
    const username = data.get('username');
    const password = data.get('password');

    try {
      const response = await fetch(`${API_BASE_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username,
          password,
          cf_response: cfToken,
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // 登錄成功，保存token
        if (rememberMe) {
          localStorage.setItem('access_token', result.access_token);
        } else {
          sessionStorage.setItem('access_token', result.access_token);
        }
        
        setAlertMessage('登錄成功！正在跳轉...');
        setAlertSeverity('success');
        
        // 跳轉到主頁或其他頁面
        setTimeout(() => {
          window.location.href = '/dashboard';
        }, 1500);
      } else {
        // 處理具體錯誤信息
        let errorMessage = '登錄失敗';
        
        if (result.detail) {
          // 如果 detail 是字符串，直接使用
          if (typeof result.detail === 'string') {
            errorMessage = result.detail;
          } 
          // 如果 detail 是數組（Pydantic 驗證錯誤）
          else if (Array.isArray(result.detail)) {
            errorMessage = result.detail.map(err => err.msg || err.message || '驗證錯誤').join(', ');
          }
          // 如果 detail 是對象
          else if (typeof result.detail === 'object') {
            errorMessage = result.detail.message || result.detail.msg || '驗證錯誤';
          }
        } else {
          // 根據狀態碼設置默認錯誤信息
          switch (response.status) {
            case 401:
              errorMessage = '用戶名或密碼錯誤';
              break;
            case 403:
              errorMessage = '帳號已被禁止登錄';
              break;
            case 400:
              errorMessage = '請求參數錯誤';
              break;
            case 422:
              errorMessage = '輸入格式不正確';
              break;
            default:
              errorMessage = '登錄失敗，請稍後重試';
          }
        }
        
        setAlertMessage(errorMessage);
        setAlertSeverity('error');
        // 登录失败时重置验证码
        setCfToken('');
      }
    } catch (error) {
      console.error('登錄錯誤:', error);
      setAlertMessage('網絡錯誤，請稍後重試');
      setAlertSeverity('error');
      // 网络错误时重置验证码
      setCfToken('');
    } finally {
      setLoading(false);
    }
  };

  // 简化的脚本加载逻辑（无需 CSP）
  React.useEffect(() => {
    // 移除可能存在的 CSP meta 标签（如果它阻止了正常功能）
    const cspMeta = document.querySelector('meta[http-equiv="Content-Security-Policy"]');
    if (cspMeta && cspMeta.content.includes('challenges.cloudflare.com')) {
      console.warn('检测到可能阻止功能的 CSP，建议移除或调整');
    }
    
    // 检查脚本是否已经存在
    const existingScript = document.querySelector('script[src*="turnstile"]');
    if (existingScript) {
      // 等待脚本加载完成
      if (window.turnstile) {
        setScriptLoaded(true);
      } else {
        existingScript.onload = () => setScriptLoaded(true);
      }
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.crossOrigin = 'anonymous';  // 添加跨域属性
    
    script.onload = () => {
      console.log('Turnstile script loaded successfully');
      setScriptLoaded(true);
    };
    
    script.onerror = (error) => {
      console.error('Failed to load Turnstile script:', error);
      setAlertMessage('验证组件加载失败，请检查网络连接或刷新页面重试');
      setAlertSeverity('error');
    };
    
    document.head.appendChild(script);
    
    return () => {
      // 清理时不移除脚本，避免重复加载问题
      // 脚本可以被其他组件复用
    };
  }, []);

  // 检查是否已登錄
  React.useEffect(() => {
    const token = localStorage.getItem('access_token') || sessionStorage.getItem('access_token');
    if (token) {
      // 可以在這裡驗證token的有效性
      // 如果有效，直接跳轉到dashboard
      window.location.href = '/dashboard';
    }
  }, []);

  return (
    <AppTheme {...props}>
      <CssBaseline enableColorScheme />
      <SignInContainer direction="column" justifyContent="space-between">
        <ColorModeSelect sx={{ position: 'fixed', top: '1rem', right: '1rem' }} />
        <Card variant="outlined">
          <Sitemark />
          <Typography
            component="h1"
            variant="h4"
            sx={{ width: '100%', fontSize: 'clamp(2rem, 10vw, 2.15rem)' }}
          >
            登入
          </Typography>

          {alertMessage && (
            <Alert severity={alertSeverity} sx={{ mb: 2 }}>
              {alertMessage}
            </Alert>
          )}

          <Box
            component="form"
            onSubmit={handleSubmit}
            noValidate
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: '100%',
              gap: 2,
            }}
          >
            <FormControl>
              <FormLabel htmlFor="username">用戶名</FormLabel>
              <TextField
                error={usernameError}
                helperText={usernameErrorMessage}
                id="username"
                type="text"
                name="username"
                placeholder="請輸入用戶名"
                autoComplete="username"
                autoFocus
                required
                fullWidth
                variant="outlined"
                color={usernameError ? 'error' : 'primary'}
                disabled={loading}
              />
            </FormControl>

            <FormControl>
              <FormLabel htmlFor="password">密碼</FormLabel>
              <TextField
                error={passwordError}
                helperText={passwordErrorMessage}
                name="password"
                placeholder="密碼須由字母數字特殊字符@$!%*?&,構成且不小於13位"
                type="password"
                id="password"
                autoComplete="current-password"
                required
                fullWidth
                variant="outlined"
                color={passwordError ? 'error' : 'primary'}
                disabled={loading}
              />
            </FormControl>

            {/* 改进的 Cloudflare 真人驗證 */}
            <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
              {scriptLoaded ? (
                <CloudflareTurnstile
                  onVerify={handleCloudflareCBSuccess}
                  onError={handleCloudflareError}
                />
              ) : (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CircularProgress size={20} />
                  <Typography variant="body2" color="text.secondary">
                    正在加载验证组件...
                  </Typography>
                </Box>
              )}
            </Box>

            <FormControlLabel
              control={
                <Checkbox
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  color="primary"
                  disabled={loading}
                />
              }
              label="記住我（7天免登錄）"
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading || !cfToken}
              sx={{ position: 'relative' }}
            >
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{
                    position: 'absolute',
                    left: '50%',
                    marginLeft: '-10px',
                  }}
                />
              )}
              {loading ? '登錄中...' : '登入'}
            </Button>
          </Box>

          <Divider>或</Divider>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Typography sx={{ textAlign: 'center' }}>
              沒有帳戶？{' '}
              <Link
                href="/signup"
                variant="body2"
                sx={{ alignSelf: 'center' }}
              >
                請先註冊
              </Link>
            </Typography>
          </Box>
        </Card>
      </SignInContainer>
    </AppTheme>
  );
}